import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { fetchFeatureFlags } from '../lib/featureFlags';
import { resolveFeatureFlagDefault } from '../lib/featureFlagDefaults';
import { getOrgId } from '../services/api';

type FeatureFlagContextValue = {
  flags: Record<string, boolean>;
  loading: boolean;
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

/**
 * Provider that fetches feature flags when org is available and exposes
 * flags, isEnabled(key), and refresh(). Renders children immediately; loading state is
 * available for components that want to show a skeleton.
 *
 * `loading` stays true until we know the org id (from getOrgId) and, when an org exists,
 * until the flag request finishes — so PageGuard does not redirect to "/" on refresh
 * while orgId is still null on the first tick.
 */
export function FeatureFlagProvider({ children }: any) {
  const [orgId, setOrgId] = useState<any>(null);
  const [orgResolved, setOrgResolved] = useState(false);
  const [flags, setFlags] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getOrgId()
      .then((id) => {
        if (!cancelled) setOrgId(id);
      })
      .catch(() => {
        if (!cancelled) setOrgId(null);
      })
      .finally(() => {
        if (!cancelled) setOrgResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchFeatureFlags();
      setFlags(next);
    } catch {
      setFlags({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!orgResolved) return;

    if (!orgId) {
      setFlags({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchFeatureFlags()
      .then((next) => {
        if (!cancelled) setFlags(next);
      })
      .catch(() => {
        if (!cancelled) setFlags({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, orgResolved]);

  const isEnabled = useCallback(
    (key) => {
      if (typeof key !== 'string') return false;
      return flags[key] === true;
    },
    [flags]
  );

  const refresh = useCallback(() => {
    return loadFlags();
  }, [loadFlags]);

  const value = useMemo(
    () => ({
      flags,
      loading,
      isEnabled,
      refresh,
    }),
    [flags, loading, isEnabled, refresh]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  return ctx;
}

/**
 * @param {string} key - Feature flag key (e.g. 'ui.dashboard.doraSection').
 * @param {boolean} [defaultValue=false] - Value when flag is missing or loading.
 * @returns {boolean}
 */
export function useFeatureFlag(key, defaultValue = false) {
  const { flags, loading } = useFeatureFlags();
  const fallback = resolveFeatureFlagDefault(key, defaultValue);
  if (loading || typeof key !== 'string') return fallback;
  if (Object.prototype.hasOwnProperty.call(flags, key)) {
    return flags[key] === true;
  }
  return fallback;
}

/**
 * Renders children only when the given feature flag is enabled; otherwise returns null.
 * Use to hide UI sections when the flag is off.
 *
 * @param {object} props
 * @param {string} props.flag - Feature flag key (e.g. 'ui.dashboard.doraSection').
 * @param {React.ReactNode} props.children
 */
export function FeatureGate({ flag, children }: any) {
  const enabled = useFeatureFlag(flag, false);
  if (!enabled) return null;
  return <>{children}</>;
}
